import Vue from 'vue';
import { keys, forEach, map, set } from 'lodash';
import { apolloClient } from '@/plugins/vue-apollo';
import {
  MRO_SEARCH_INFORMATION_REQUESTS_QUERY,
  MRO_GET_INFORMATION_REQUEST_QUERY,
  MRO_GET_COMPARER_RESPONSES,
} from '@/store/graphql/queries/cat4mro';
import {
  SEND_ITEM_PURCHASE_THREAD_RESPONSE_MUTATION,
  UPDATE_ITEM_PURCHASE_THREAD_RESPONSE_MUTATION,
  INITIALIZE_ITEM_PURCHASE_THREAD_RESPONSE_MUTATION,
} from '@/store/graphql/mutations/request-information';

const requestInformation = {
  namespaced: true,
  state: {
    request: null,
    search: {
      count: 0,
      data: [],
    },
  },
  actions: {
    async search({ commit }, filters) {
      try {
        const searchRequestQuery = {
          query: MRO_SEARCH_INFORMATION_REQUESTS_QUERY,
          variables: {
            limit: filters.limit,
            offset: filters.offset,
            order_by: { created_at: 'desc' },
          },
        };

        if (filters.keywords && filters.keywords.length) {
          searchRequestQuery.variables.keywords = `%${filters.keywords}%`;
        }

        if (filters.order_by) {
          searchRequestQuery.variables.order_by = filters.order_by;
        }

        const response = await apolloClient.query(searchRequestQuery);
        if (response && response.data) {
          const searchResult = {
            data: response.data.information_request,
            count: response.data.information_request_aggregate.aggregate.count,
          };
          commit('SET_SEARCH_RESULT', searchResult);
        }
      } catch (error) {
        throw new Error(error);
      }
    },
    async get({ commit }, requestId) {
      try {
        const requestQuery = {
          query: MRO_GET_INFORMATION_REQUEST_QUERY,
          variables: {
            id: requestId,
          },
        };

        const response = await apolloClient.query(requestQuery);
        if (response && response.data) {
          commit('SET_REQUEST', response.data.information_request_by_pk);
        }
      } catch (error) {
        throw new Error(error);
      }
    },
    async getComparer({ commit }, request) {
      try {
        const requestQuery = {
          query: MRO_GET_COMPARER_RESPONSES,
          variables: {
            id: request.id,
            require_stock: request.require_stock_confirmation ? false : null,
            require_delivery_availability: request.require_delivery_availability ? false : null,
          },
        };

        const response = await apolloClient.query(requestQuery);
        window.console.log(response);
        if (response && response.data) {
          commit('SET_COMPARER_DATA', response.data.information_response);
        }
      } catch (error) {
        throw new Error(error);
      }
    },
    // TODO: migrate to request-information supplier
    async initializeResponse({ commit }, threadId) {
      try {
        const readedAt = new Date().toISOString();
        const readedMutation = {
          mutation: INITIALIZE_ITEM_PURCHASE_THREAD_RESPONSE_MUTATION,
          variables: {
            id: threadId,
            readed_at: readedAt,
          },
        };

        await apolloClient.mutate(readedMutation);
        commit('SET_READED', { readed_at: readedAt });
      } catch (error) {
        throw new Error(error);
      }
    },
    // TODO: migrate to request-information supplier
    async send({ commit }, { responseId }) {
      try {
        const answredAt = new Date().toISOString();
        const sendMutation = {
          mutation: SEND_ITEM_PURCHASE_THREAD_RESPONSE_MUTATION,
          variables: {
            id: responseId,
            answered_at: answredAt,
          },
        };

        const { data } = await apolloClient.mutate(sendMutation);
        if (data.update_information_response.affected_rows > 0) {
          commit('SET_ANSWERED', { answered_at: answredAt });
        }
      } catch (error) {
        throw new Error(error);
      }
    },
    async updateResponse(store, { request, response }) {
      try {
        const responseItemsMap = map(response.items, (item) => {
          const itemMap = {
            id: item.id,
            price: item.catalog_item.price,
            currency: item.catalog_item.currency,
            has_stock: item.has_stock,
            has_stock_alternative: item.has_stock_alternative, // TODO: crear columna hasura
            stock_quantity: request.requested_quantity,
            stock_uom: request.requested_uom,
            has_delivery_availability: item.has_delivery_availability,
            has_delivery_alternative: item.has_delivery_alternative, // TODO: crear columna hasura
            delivery_location: request.expected_delivery_location, // TODO: crear columna hasura
            delivery_date: request.expected_delivery_date,
            delivery_observations: item.delivery_observations,
          };

          if (item.price && item.currency) {
            itemMap.price = item.price;
            itemMap.currency = item.currency;
          }

          if (itemMap.has_stock_alternative === true) {
            itemMap.stock_quantity = item.stock_quantity;
            itemMap.stock_uom = item.stock_uom;
          }

          if (itemMap.has_delivery_alternative === true) {
            itemMap.delivery_date = item.delivery_date;
          }

          itemMap.score = 0;
          itemMap.average = 0;
          return itemMap;
        });

        const query = UPDATE_ITEM_PURCHASE_THREAD_RESPONSE_MUTATION({ id: response.id, updated_at: new Date().toISOString() }, responseItemsMap);
        const updateResponseMutation = {
          mutation: query.mutation,
          variables: query.variables,
        };

        await apolloClient.mutate(updateResponseMutation);
      } catch (error) {
        window.console.log(error);
        throw new Error(error);
      }
    },
  },
  mutations: {
    SET_SEARCH_RESULT(state, { data, count }) {
      const mapData = map(data, (request) => {
        if (new Date(request.close_date) < new Date()) {
          set(request, 'status', 2);
        } else {
          set(request, 'status', 1);
        }
        return request;
      });

      Vue.set(state.search, 'data', mapData);
      Vue.set(state.search, 'count', count || 0);
    },
    SET_REQUEST(state, request) {
      if (new Date(request.close_date) < new Date()) {
        set(request, 'status', 2);
      } else {
        set(request, 'status', 1);
      }
      Vue.set(state, 'request', request);
    },
    UPDATE_REQUEST(state, request) {
      const objectKeys = keys(request);
      forEach(objectKeys, (objectKey) => {
        const objectValue = request[objectKey];
        Vue.set(state.request, objectKey, objectValue);
      });
    },
    SET_ANSWERED(state, answeredAt) {
      if (state.request && (state.request.supplier_responses && state.request.supplier_responses.length)) {
        Vue.set(state.request.supplier_responses[0], 'answered_at', answeredAt);
      }
    },
    SET_READED(state, readedAt) {
      if (state.request && (state.request.supplier_responses && state.request.supplier_responses.length)) {
        Vue.set(state.request.supplier_responses[0], 'readed_at', readedAt);
      }
    },
    SET_REQUEST_RESPONSE(state, response) {
      Vue.set(state.request, 'response', response);
    },
    SET_COMPARER_DATA(state, data) {
      Vue.set(state.request, 'comparer', data);
    },
  },
  getters: {
    searchInfo: state => state.search,
    requestInfo: state => state.request,
  },
};

export default requestInformation;



// WEBPACK FOOTER //
// ./src/store/modules/buyer/request-information.js